import { useState } from 'react';
import { Icons } from '@/utils/Icons';
import { useLazyGetMatchesQuery, useLazyGetSportCompetitionsQuery } from '@/store/api/sportsApi';
import { useNavigate } from 'react-router-dom';
import SearchDropdown from '@/components/common/SearchDropdown';

/* ================= TYPES ================= */

type TreeNode = {
  id: string;
  label: string;
  children?: TreeNode[];
  sportName?: string;
  seriesId?: string;
  path?: string;
};

type SidebarProps = {
  isOpen: boolean;
  onToggle: () => void;
};

/* ================= COMPONENT ================= */

const Sidebar = ({ isOpen, onToggle }: SidebarProps) => {
  const navigate = useNavigate();
  /* ---------- API ---------- */

  const [getCompetitions] = useLazyGetSportCompetitionsQuery();
  const [getMatches] = useLazyGetMatchesQuery();

  /* ---------- STATE ---------- */

  const [openSections, setOpenSections] = useState({
    racing: true,
    others: true,
    allSports: true,
  });

  // Track which sport node is open (only one at a time)
  const [activeSportId, setActiveSportId] = useState<string | null>(null);

  // Track which competition node is open (only one at a time)
  const [activeCompetitionId, setActiveCompetitionId] = useState<string | null>(null);

  const [sportCompetitions, setSportCompetitions] = useState<Record<string, TreeNode[]>>({});

  const [competitionMatches, setCompetitionMatches] = useState<Record<string, TreeNode[]>>({});

  /* ================= STATIC TREES ================= */

  const racingTree: TreeNode[] = [
    { id: 'horse', label: 'Horse Racing' },
    { id: 'greyhound', label: 'Greyhound Racing' },
  ];

  const othersTree: TreeNode[] = [
    {
      id: 'casino',
      label: 'Our Casino',
      path: '/casino/Mac88/All',
    },
    {
      id: 'vip',
      label: 'Our VIP Casino',
      path: '/casino/mac88?search=20 20 teenpatti',
    },
    {
      id: 'premium',
      label: 'Our Premium Casino',
      path: '/casino/Vivo Gaming/All',
    },
    {
      id: 'virtual',
      label: 'Our Virtual',
      path: '/casino/Mac88/Virtual',
    },
    {
      id: 'live',
      label: 'Live Casino',
      path: '/casino/Evolution Gaming/Live Roulette',
    },
    {
      id: 'slot',
      label: 'Slot Game',
      path: '/casino/JDB Gaming/Video Slots',
    },
    {
      id: 'fantasy',
      label: 'Fantasy Game',
      path: '/casino/Smartsoft Gaming/Crash Games',
    },
  ];

  const sportsTree: TreeNode[] = [
    { id: 'politics', label: 'Politics' },
    { id: 'cricket', label: 'Cricket' },
    { id: 'football', label: 'Football' },
    { id: 'tennis', label: 'Tennis' },
    { id: 'table-tennis', label: 'Table Tennis' },
    { id: 'badminton', label: 'Badminton' },
    { id: 'esoccer', label: 'Esoccer' },
    { id: 'basketball', label: 'Basketball' },
    { id: 'volleyball', label: 'Volleyball' },
    { id: 'snooker', label: 'Snooker' },
    { id: 'ice-hockey', label: 'Ice Hockey' },
    { id: 'e-games', label: 'E Games' },
    { id: 'futsal', label: 'Futsal' },
    { id: 'handball', label: 'Handball' },
    { id: 'kabaddi', label: 'Kabaddi' },
    { id: 'golf', label: 'Golf' },
    { id: 'rugby-league', label: 'Rugby League' },
    { id: 'boxing', label: 'Boxing' },
    { id: 'beach-volleyball', label: 'Beach Volleyball' },
    { id: 'mma', label: 'Mixed Martial Arts' },
    { id: 'motogp', label: 'MotoGP' },
    { id: 'chess', label: 'Chess' },
    { id: 'cycling', label: 'Cycling' },
    { id: 'motorbikes', label: 'Motorbikes' },
    { id: 'athletics', label: 'Athletics' },
    { id: 'basketball-3x3', label: 'Basketball 3X3' },
    { id: 'sumo', label: 'Sumo' },
    { id: 'virtual-sports', label: 'Virtual sports' },
    { id: 'motor-sports', label: 'Motor Sports' },
    { id: 'baseball', label: 'Baseball' },
    { id: 'rugby-union', label: 'Rugby Union' },
    { id: 'darts', label: 'Darts' },
    { id: 'american-football', label: 'American Football' },
    { id: 'soccer', label: 'Soccer' },
    { id: 'esports', label: 'Esports' },
    { id: 'boat-racing', label: 'Boat Racing' },
    { id: 'wrestling', label: 'Wrestling' },
  ];

  /* ================= HELPERS ================= */

  const toggleSection = (key: keyof typeof openSections) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  /* ================= API TREE LOGIC ================= */

  const toggleNode = async (node: TreeNode, level: number) => {
    // SPORT LEVEL (level 0) — only one sport open at a time
    if (level === 0) {
      const isAlreadyOpen = activeSportId === node.id;

      if (isAlreadyOpen) {
        // Close the sport and clear its active competition
        setActiveSportId(null);
        setActiveCompetitionId(null);
        return;
      }

      // Switch to a new sport — close previous competition too
      setActiveSportId(node.id);
      setActiveCompetitionId(null);

      // Fetch competitions only if not already loaded
      if (!sportCompetitions[node.id]) {
        const res = await getCompetitions(node.label).unwrap();

        const competitions =
          res?.competitions?.map((comp) => ({
            id: `comp-${comp.id}`,
            label: comp.name,
            sportName: node.label,
            seriesId: String(comp.id),
          })) || [];

        setSportCompetitions((prev) => ({
          ...prev,
          [node.id]: competitions,
        }));
      }

      return;
    }

    // COMPETITION LEVEL (level 1) — only one competition open at a time
    if (level === 1 && node.seriesId) {
      const isAlreadyOpen = activeCompetitionId === node.seriesId;

      if (isAlreadyOpen) {
        setActiveCompetitionId(null);
        return;
      }

      setActiveCompetitionId(node.seriesId);

      // Fetch matches only if not already loaded
      if (!competitionMatches[node.seriesId]) {
        const res = await getMatches({
          sportName: node.sportName?.toLowerCase(),
          seriesId: node.seriesId,
        }).unwrap();

        const matches =
          res?.fixture?.map((match) => ({
            id: String(match.id),
            label: match.name,
            sportName: match.sport,
          })) || [];

        setCompetitionMatches((prev) => ({
          ...prev,
          [node.seriesId!]: matches,
        }));
      }
    }
  };

  /* ================= TREE ITEM ================= */

  const TreeItem = ({
    node,
    level = 0,
    section,
  }: {
    node: TreeNode;
    level?: number;
    section: 'racing' | 'others' | 'allSports';
  }) => {
    const isOpen =
      level === 0
        ? activeSportId === node.id
        : level === 1
          ? activeCompetitionId === node.seriesId
          : false;

    const dynamicChildren =
      level === 0
        ? sportCompetitions[node.id]
        : level === 1 && node.seriesId
          ? competitionMatches[node.seriesId]
          : undefined;

    const children = dynamicChildren || node.children;

    const showToggleIcon = section === 'allSports' && (level === 0 || level === 1);
    const paddingLeft = level === 0 ? 15 : level === 1 ? 25 : 35;

    return (
      <div>
        <button
          onClick={() => {
            if (section == 'others') {
              navigate(node?.path ?? '#');
            }
            if (level === 2 && node.sportName) {
              navigate(`/game-details/${node.sportName}/${node.id}`);
              return;
            }
            if (showToggleIcon) toggleNode(node, level);
          }}
          className="text-start w-full min-h-[28px] bg-sidebar-item text-[14px]
          flex items-center gap-2 border-b border-sidebar-item-border"
          style={{ paddingLeft: `${paddingLeft}px` }}
        >
          {showToggleIcon &&
            (isOpen ? <Icons.MinusSquare size={12} /> : <Icons.PlusSquare size={12} />)}

          {node.label}
        </button>

        {isOpen &&
          children?.map((child) => (
            <TreeItem key={child.id} node={child} level={level + 1} section={section} />
          ))}
      </div>
    );
  };

  /* ================= SECTION HEADER ================= */

  const SectionHeader = ({
    title,
    sectionKey,
  }: {
    title: string;
    sectionKey: keyof typeof openSections;
  }) => (
    <div
      onClick={() => toggleSection(sectionKey)}
      className="h-[32px] bg-primary text-body-inverse text-[15px]
      font-semibold flex items-center justify-between px-3 cursor-pointer"
    >
      {title}
      {openSections[sectionKey] ? <Icons.Up size={12} /> : <Icons.Down size={12} />}
    </div>
  );

  /* ================= UI ================= */

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-secondary/40 z-40 xl:hidden" onClick={onToggle} />}

      <aside
        className={`
          fixed xl:static
          top-0 left-0
          h-full
          w-[280px]
          xl:w-[240px]
          2xl:w-[280px]
          bg-sidebar-bg
          border-r
          overflow-y-auto
          z-50
          transform transition-transform duration-300
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          xl:translate-x-0
        `}
      >
        <div className="flex justify-between p-2 xl:hidden bg-surface-sidebar-mobile">
          {/* Search Bar */}
          <div className="w-[80%]">
            <SearchDropdown key={String(isOpen)} />
          </div>
          <button onClick={onToggle}>
            <Icons.Close className="font-bold" size={24} />
          </button>
        </div>
        <div className="text-sm">
          <SectionHeader title="Racing Sports" sectionKey="racing" />
          {openSections.racing &&
            racingTree.map((node) => <TreeItem key={node.id} node={node} section="racing" />)}

          <SectionHeader title="Others" sectionKey="others" />
          {openSections.others &&
            othersTree.map((node) => <TreeItem key={node.id} node={node} section="others" />)}

          <SectionHeader title="All Sports" sectionKey="allSports" />
          {openSections.allSports &&
            sportsTree.map((node) => <TreeItem key={node.id} node={node} section="allSports" />)}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
